import { createPdfHelpers, uploadAndRegisterPdf } from './gestion-tecnica-documentos'
import { SnookCirielloAssessmentRow } from './gestion-tecnica-snook-ciriello'
import { ResultadoSnookTransporte, ResultadoSnookEmpujeArrastre } from './snook-ciriello'

// Genera el reporte técnico PDF de una evaluación Snook y Ciriello, siguiendo
// el mismo patrón que generateRebaReportPDF(): createPdfHelpers() para el
// flujo de texto + uploadAndRegisterPdf() para subir el blob y registrarlo
// en sso_documents.
//
// checklist_item_id: 'GT-06' — mismo ítem genérico que RULA/REBA/NIOSH
// (confirmado en compliance-engine.ts: "Informe de evaluación de riesgos
// ergonómicos y psicosociales", Decisión 584 Art. 11, DE 255 (2024) Art.
// 44/45/46, AM 196 (2024)) — sin atadura a una metodología específica.
export async function generateSnookCirielloReportPDF(
  evaluacion: SnookCirielloAssessmentRow,
  companyId: string,
  companyName: string,
  uploadedBy: string | null
): Promise<string | null> {
  const helpers = createPdfHelpers()
  const { txt, hline, doc } = helpers

  const { inputs, resultado, tipo_tarea } = evaluacion

  // a. Cabecera
  txt(companyName, 14, 'bold', 'center', 0, 2)
  txt('Reporte de Evaluación Ergonómica — Tablas de Snook y Ciriello', 12, 'bold', 'center', 0, 6)
  txt(evaluacion.descripcion, 11, 'bold', 'center', 0, 4)
  txt('Snook, S.H. y Ciriello, V.M. (1991) — valores del percentil 90 según calculadora oficial del INSST', 9, 'italic', 'center', 0, 6)
  hline()

  // b. Datos de entrada
  txt('Datos de entrada', 11, 'bold', 'left', 0, 4)
  txt(`Tipo de tarea: ${tipo_tarea.charAt(0).toUpperCase()}${tipo_tarea.slice(1)}`, 9, 'normal', 'left', 0, 2)
  txt(`Sexo: ${evaluacion.sexo === 'masculino' ? 'Masculino' : 'Femenino'}`, 9, 'normal', 'left', 0, 2)
  txt(`Distancia: ${inputs.distancia} m`, 9, 'normal', 'left', 0, 2)
  txt(`Altura: ${inputs.altura} cm`, 9, 'normal', 'left', 0, 2)
  txt(`Frecuencia: ${inputs.frecuencia}`, 9, 'normal', 'left', 0, 2)
  txt(`Carga sin asas o con agarre deficiente: ${inputs.sinAsasOAgarreDeficiente ? 'sí' : 'no'}`, 9, 'normal', 'left', 0, 2)
  txt(`Carga manejada alejada del cuerpo: ${inputs.cargaAlejadaDelCuerpo ? 'sí' : 'no'}`, 9, 'normal', 'left', 0, 4)
  hline()

  // c. Resultado — ramificado según tipo_tarea
  txt('Resultado', 11, 'bold', 'left', 0, 4)

  if (tipo_tarea === 'transporte') {
    const r = resultado as ResultadoSnookTransporte
    txt(`Peso máximo aceptable (corregido): ${r.maximoAceptable} kg`, 9, 'normal', 'left', 0, 2)
    txt(`Peso real: ${r.pesoReal} kg`, 9, 'normal', 'left', 0, 3)
    txt(`Resultado: ${r.aceptable ? 'Aceptable' : 'No aceptable'}`, 10, 'bold', 'left', 0, 4)
  } else {
    const r = resultado as ResultadoSnookEmpujeArrastre
    txt(`Fuerza inicial máxima (corregida): ${r.inicialMaxima} kgf — Fuerza real: ${r.fuerzaInicialReal} kgf — Aceptable: ${r.aceptableInicial ? 'sí' : 'no'}`, 9, 'normal', 'left', 0, 2)
    txt(`Fuerza sostenida máxima (corregida): ${r.sostenidaMaxima} kgf — Fuerza real: ${r.fuerzaSostenidaReal} kgf — Aceptable: ${r.aceptableSostenida ? 'sí' : 'no'}`, 9, 'normal', 'left', 0, 3)
    txt(`Resultado: ${r.aceptableInicial && r.aceptableSostenida ? 'Aceptable' : 'No aceptable'}`, 10, 'bold', 'left', 0, 4)
  }
  hline()

  // d. Disclaimer — mismo criterio que RULA/REBA: el PDF documenta
  // exactamente lo que ve el usuario en pantalla, no una versión distinta.
  txt(
    'Valores extraídos de la calculadora oficial del INSST (Instituto Nacional de ' +
    'Seguridad y Salud en el Trabajo, España), "Manipulación manual: empuje, arrastre y ' +
    'transporte" (2020, NIPO 118-20-079-6), basada en Snook, S.H. y Ciriello, V.M. (1991). ' +
    'Alcance de este método: solo percentil 90 (resultado aceptable para el 90% de la ' +
    'población), sin el nivel intermedio "mejorable" del método académico completo. ' +
    'Este reporte no reemplaza el juicio de un profesional de ergonomía para el rediseño de la tarea.',
    8, 'italic', 'left', 0, 4
  )

  const blob = doc.output('blob')
  const filename = `SNOOK_CIRIELLO_${evaluacion.descripcion.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
  doc.save(filename)

  return uploadAndRegisterPdf(companyId, 'gestion-tecnica', `${evaluacion.id}-snook-ciriello`, blob, filename, {
    category: 'procedimiento',
    title: `Reporte Snook y Ciriello - ${evaluacion.descripcion}`,
    description: 'Generado automáticamente desde Metodología Snook y Ciriello',
    checklist_item_id: 'GT-06',
    uploaded_by: uploadedBy,
  })
}
